import { AuthContext } from "@@components";
import { MainLayout } from "@@containers";
import React, { useContext, useMemo, useRef } from "react";
import { createBrowserRouter, LoaderFunctionArgs, redirect, RouteObject, RouterProvider } from "react-router-dom";
import Spinner from "../../common/components/Spinner";
import ExpressionEvaluatorContext from "../../ExpressionsModule/Contexts/ExpressionEvaluatorContext";
import { HomeView } from "../../views";
import NotFoundView from "../../views/NotFound/NotFoundView";
import ViewComponent from "../Components/View/ViewComponent";
import BundleContext from "../Contexts/BundleContext";
import { NavigationService } from "../Services/NavigationService";

const DynamicRouteContainer: React.FC = () => {
  const auth = useContext(AuthContext);
  const bundle = useContext(BundleContext);
  const expressionContext = useContext(ExpressionEvaluatorContext);
  const hasInitialized = useRef(false);

  const router = useMemo(() => {
    // Mark as initialized on first router creation
    if (!hasInitialized.current) {
      hasInitialized.current = true;
    }
    function protectedLoader({ request }: LoaderFunctionArgs) {
      if (!auth.isAuthenticated) {
        const params = new URLSearchParams();
        params.set("from", new URL(request.url).pathname);
        return redirect(`/?${params.toString()}`);
      }
      return null;
    }

    const children: RouteObject[] = bundle.Views.filter((view) => view.RoutePath !== undefined).map((view) => ({
      id: view.Id,
      path: view.RoutePath,
      loader: view.RequiresAuthentication ? protectedLoader : undefined,
      element: <ViewComponent key={view.Id} component={view} />,
    }));

    if (children.length === 0) {
      children.push({
        path: "/",
        loader: undefined,
        element: <HomeView />,
      });
    }

    const createdRouter = createBrowserRouter([
      {
        id: "root",
        path: "/",
        Component: MainLayout,
        children: children,
        errorElement: <NotFoundView />,
      },
      {
        path: "*",
        Component: MainLayout,
        children: [
          {
            path: "*",
            element: <NotFoundView />,
          },
        ],
      },
    ]);

    const navService = expressionContext?.GetProperty<NavigationService>(NavigationService.KEY);
    navService?.setNavigate((path) => createdRouter.navigate(path));

    return createdRouter;
  }, [auth.isAuthenticated, bundle.Views, expressionContext]);

  // Don't render the router until it has been initialized with routes
  if (!hasInitialized.current) {
    return <Spinner />;
  }

  return <RouterProvider router={router} />;
};

export default DynamicRouteContainer;
